import * as React from 'react'

import { Box, InputCheckBox, SelectOption, Text } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'

import { observer } from 'mobx-react'

import Select from 'modules/core/components/Inputs/Select'
import TextInput from 'modules/core/components/Inputs/Text'
import MultiInput from 'modules/core/components/MultiInput'
import { type FeatureAvailabilityType } from 'modules/core/hooks/useFeatureAvailability'
import i18n from 'modules/core/lib/i18n'
import { isFeatureAvailabilityEnabled } from 'modules/core/lib/isFeatureAvailabilityEnabled'
import companies from 'modules/core/store/collections/companies'
import { companyLegalEntities } from 'modules/core/store/collections/legalEntities'
import type LegalEntity from 'modules/core/store/models/LegalEntity'

import { InformationTooltip } from 'modules/timeoff/components/TimeoffAllowances/UpsertAllowance/AllowanceSettings/_InformationTooltip'
import type AllowanceForm from 'modules/timeoff/lib/AllowanceForm'
import {
  availabilityOptionsForFixedBalanceLabels,
  availableOptionsDaysLabels,
  availableOptionsHoursLabels,
  daysTypeOptionsLabel,
  hoursTypeOptionsLabel,
  prorationTypeDaysOptionsLabel,
  prorationTypeHoursOptionsLabel,
} from 'modules/timeoff/lib/dynamicTranslations'
import { AvailabilityOptionsForFixedBalance } from 'modules/timeoff/lib/enums'
import Allowance, { EMPLOYEE_HIRED_DATE } from 'modules/timeoff/store/models/Allowance'

interface Props {
  allowanceForm: AllowanceForm
  isHourly: boolean
  featureAvailabilities: FeatureAvailabilityType[]
  // TODO: Remove countSmRestDaysOptionAvailable once Feature Flag is not needed
  countSmRestDaysOptionAvailable: boolean
}

const dayTypes = (featureAvailabilities: FeatureAvailabilityType[]): string[] => {
  const supportsWorkableDays = companyLegalEntities(companies.current).some(
    (legalEntity: LegalEntity) => Allowance.FRANCE_WITH_DEPARTMENTS[legalEntity.country]
  )
  const options = [Allowance.DAYS_TYPE.working_days, Allowance.DAYS_TYPE.natural_days]

  if (!supportsWorkableDays) return options

  options.push(Allowance.DAYS_TYPE.french_calendar_days)

  const ouvresEnabled = isFeatureAvailabilityEnabled(
    featureAvailabilities,
    Features.timeoff_french_ouvres
  )

  if (ouvresEnabled) options.push(Allowance.DAYS_TYPE.french_ouvres)

  return options
}

const BaseOnFixedBalance = ({
  allowanceForm,
  isHourly,
  featureAvailabilities,
  // TODO: Remove countSmRestDaysOptionAvailable once Feature Flag is not needed
  countSmRestDaysOptionAvailable,
}: Props) => {
  const daysTypeOptions = dayTypes(featureAvailabilities).reduce(
    (result: SelectOption[], value: string) => {
      if (isHourly) {
        if (
          value !== Allowance.DAYS_TYPE.french_calendar_days &&
          value !== Allowance.DAYS_TYPE.french_ouvres
        ) {
          result.push({
            label: hoursTypeOptionsLabel(value),
            value,
          })
        }
      } else {
        result.push({
          label: daysTypeOptionsLabel(value),
          value,
        })
      }

      return result
    },
    []
  )

  const showAllOptions = isFeatureAvailabilityEnabled(
    featureAvailabilities,
    Features.timeoff_accrual_frequency_options
  )
  const availableOptions = showAllOptions
    ? AvailabilityOptionsForFixedBalance
    : Allowance.AVAILABLE_OPTIONS_DAYS

  // Don't show or accept mensiversary option if cycle is not by contract start

  const isEmployeeHiredCycle =
    allowanceForm.get('cycle_start').value === Allowance.CYCLE_START.employee_hired_date ||
    allowanceForm.get('timeoff_cycle').value === EMPLOYEE_HIRED_DATE // For non-flexible cycles support

  const isAvailableDaysValid =
    isEmployeeHiredCycle ||
    allowanceForm.get('available_days').value != AvailabilityOptionsForFixedBalance.Mensiversary

  if (!isAvailableDaysValid) {
    allowanceForm.setErrors({
      available_days: [i18n.t('time_off.allowances.fields.available_days_errors.mensiversary')],
    })
  } else {
    allowanceForm.setErrors({ available_days: {} })
  }

  const availableOptionValues = Object.values(availableOptions).filter((value) => {
    if (value === AvailabilityOptionsForFixedBalance.Mensiversary && !isEmployeeHiredCycle) {
      return false
    }
    return true
  })

  const availableOptionsDays = availableOptionValues.map((value) => {
    const label = showAllOptions
      ? availabilityOptionsForFixedBalanceLabels(value)
      : isHourly
        ? availableOptionsHoursLabels(value)
        : availableOptionsDaysLabels(value)

    return {
      label,
      value,
    }
  })

  const renderProrationType = isFeatureAvailabilityEnabled(
    featureAvailabilities,
    Features.timeoff_allowance_proration
  )

  const renderTimeoffCountBankHolidaysAvailability = isFeatureAvailabilityEnabled(
    featureAvailabilities,
    Features.timeoff_count_bank_holidays
  )

  const allowanceIsNaturalDays =
    allowanceForm.get('days_type').value === Allowance.DAYS_TYPE.natural_days

  const rangeOptionsForNatural = [
    {
      label: i18n.t('time_off.allowances.fields.range_options.extra_non_working_days_at_end'),
      value: Allowance.RANGE_OPTIONS.extra_non_working_days_at_end,
    },
    {
      label: i18n.t('time_off.allowances.fields.range_options.exact_range'),
      value: Allowance.RANGE_OPTIONS.exact_range,
    },
  ]

  const countSMRestDaysOptions = [
    {
      label: i18n.t('time_off.allowances.fields.range_options.exact_range_working'),
      value: false,
    },
    {
      label: i18n.t('time_off.allowances.fields.range_options.include_sm_rest_days'),
      value: true,
    },
  ]

  const holidayOptions = [
    {
      label: i18n.t('time_off.allowances.fields.count_holiday_options.count_holiday_disabled'),
      value: false,
    },
    {
      label: i18n.t('time_off.allowances.fields.count_holiday_options.count_holiday_enabled'),
      value: true,
    },
  ]

  const prorationTypeDaysOptions = Object.keys(Allowance.PRORATION_TYPE).map((value) => ({
    label: prorationTypeDaysOptionsLabel(value),
    value,
  }))

  const prorationTypeHoursOptions = Object.keys(Allowance.PRORATION_TYPE).map((value) => ({
    label: prorationTypeHoursOptionsLabel(value),
    value,
  }))

  const prorationTypeOptions = isHourly ? prorationTypeHoursOptions : prorationTypeDaysOptions

  const baseAllowanceLabel = isHourly
    ? i18n.t('time_off.allowances.page.labels.base_allowance_hours')
    : i18n.t('time_off.allowances.page.labels.base_allowance_days')

  const baseAllowanceTooltip = isHourly
    ? i18n.t('time_off.allowances.page.information.base_allowance_hours')
    : i18n.t('time_off.allowances.page.information.base_allowance_days')

  const unlimitedHolidaysLabel = isHourly
    ? i18n.t('time_off.allowances.page.labels.unlimited_hours')
    : i18n.t('time_off.allowances.page.labels.unlimited_days')

  const availableDaysLabel = isHourly
    ? i18n.t('time_off.allowances.page.labels.available_hours')
    : i18n.t('time_off.allowances.page.labels.available_days')

  const prorationTypeLabel = isHourly
    ? i18n.t('time_off.allowances.page.labels.proration_type_hours')
    : i18n.t('time_off.allowances.page.labels.proration_type_days')

  const availableDaysInformation = isHourly
    ? i18n.t('time_off.allowances.page.information.available_hours')
    : i18n.t('time_off.allowances.page.information.available_days')

  const daysTypeInfo = i18n.t('time_off.allowances.page.information.days_type')

  const naturalDaysRangeLabel = i18n.t('time_off.allowances.page.labels.how_to_count_range')
  const naturalDaysRangeInfo = i18n.t('time_off.allowances.page.information.how_to_count_range')

  const countSMRestDaysLabel = i18n.t('time_off.allowances.page.labels.how_to_count_range_working')
  const countSMRestDaysInfo =
    'This setting affects employees that have marked a explicit rest day in Shift Management'

  const unlimited = i18n.t('common.unlimited')

  const maxBalanceCapLabel = isHourly
    ? i18n.t('time_off.allowances.page.labels.max_cap_hours')
    : i18n.t('time_off.allowances.page.labels.max_cap_days')

  const maxBalanceCapInfo = i18n.t('time_off.allowances.page.information.max_cap')
  const isUnlimited = allowanceForm.get('unlimited_holidays').value
  const checkUnlimitedValue = allowanceForm.get('unlimited_holidays').value ? unlimited : ''

  return (
    <>
      <Box gap="s16">
        <MultiInput label={baseAllowanceLabel} information={baseAllowanceTooltip}>
          <TextInput
            field={allowanceForm.get('holiday_allowance_in_cents')}
            readonly={isUnlimited}
            value={checkUnlimitedValue}
            extra={{ 'data-intercom-target': 'timeoff-allowances-base-allowance-input' }}
          />
          <Select
            field={allowanceForm.get('days_type')}
            secondaryLabel={<InformationTooltip label={daysTypeInfo} />}
            options={daysTypeOptions}
            extra={{ 'data-intercom-target': 'timeoff-allowances-days-type-input' }}
          />
        </MultiInput>
        <Box
          flexDirection="row"
          alignItems="center"
          gap="s8"
          extra={{ 'data-intercom-target': 'timeoff-allowances-unlimited-holidays-checkbox' }}
        >
          <InputCheckBox
            checked={allowanceForm.values.unlimited_holidays}
            onClick={() =>
              allowanceForm.setValues({
                unlimited_holidays: !allowanceForm.values.unlimited_holidays,
              })
            }
          />
          <Text.Normal>{unlimitedHolidaysLabel}</Text.Normal>
        </Box>

        {renderTimeoffCountBankHolidaysAvailability && (
          <Select
            field={allowanceForm.get('count_holiday_as_workable')}
            label={i18n.t('time_off.allowances.page.labels.count_holiday_as_workable')}
            secondaryLabel={
              <InformationTooltip
                label={i18n.t('time_off.allowances.page.information.count_holiday_as_workable')}
              />
            }
            options={holidayOptions}
            extra={{ 'data-intercom-target': 'timeoff-allowances-count-holiday-as-workable-input' }}
          />
        )}

        {allowanceIsNaturalDays && (
          <Select
            label={naturalDaysRangeLabel}
            field={allowanceForm.get('range_type')}
            options={rangeOptionsForNatural}
            secondaryLabel={<InformationTooltip label={naturalDaysRangeInfo} />}
            extra={{ 'data-intercom-target': 'timeoff-allowances-natural-days-range-input' }}
          />
        )}

        {
          // TODO: Remove countSmRestDaysOptionAvailable once Feature Flag is not needed
          !allowanceIsNaturalDays && countSmRestDaysOptionAvailable && (
            <Select
              label={countSMRestDaysLabel}
              field={allowanceForm.get('count_sm_rest_days')}
              options={countSMRestDaysOptions}
              extra={{ 'data-intercom-target': 'timeoff-allowances-natural-days-range-input' }}
              secondaryLabel={<InformationTooltip label={countSMRestDaysInfo} />}
            />
          )
        }

        {renderProrationType && (
          <Select
            label={prorationTypeLabel}
            field={allowanceForm.get('proration_type')}
            options={prorationTypeOptions}
            extra={{ 'data-intercom-target': 'timeoff-allowances-proration-type-input' }}
          />
        )}

        <TextInput
          label={maxBalanceCapLabel}
          secondaryLabel={<InformationTooltip label={maxBalanceCapInfo} />}
          placeholder={unlimited}
          field={allowanceForm.get('maximum_amount_in_cents')}
          readonly={isUnlimited}
          value={checkUnlimitedValue}
          extra={{ 'data-intercom-target': 'timeoff-allowances-max-cap-input' }}
        />
        <Select
          field={allowanceForm.get('available_days')}
          label={availableDaysLabel}
          secondaryLabel={<InformationTooltip label={availableDaysInformation} />}
          options={availableOptionsDays}
          extra={{ 'data-intercom-target': 'timeoff-allowances-available-days-input' }}
        />
      </Box>
    </>
  )
}

export default observer(BaseOnFixedBalance)
